<script setup>
import { computed, onMounted, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { useRoute, useRouter } from 'vue-router';
import { useAlert } from 'dashboard/composables';
import SettingsLayout from 'dashboard/routes/dashboard/settings/SettingsLayout.vue';
import BaseSettingsHeader from 'dashboard/routes/dashboard/settings/components/BaseSettingsHeader.vue';
import NextButton from 'dashboard/components-next/button/Button.vue';
import WabaCurrencyMigrationsAPI from 'custom/dashboard/api/wabaCurrencyMigrations';
import { migrationApiError } from 'custom/dashboard/helper/zimobi/wabaCurrencyMigrationHelper';
import ClientSettingsTabs from '../ClientSettingsTabs.vue';
import CreateMigrationDialog from './CreateMigrationDialog.vue';
import MigrationStatusBadge from './MigrationStatusBadge.vue';

const { t } = useI18n();
const route = useRoute();
const router = useRouter();

const isFetching = ref(true);
const isCreating = ref(false);
const migrations = ref([]);
const inboxes = ref([]);
const currencies = ref([]);
const createDialogRef = ref(null);

const hasEligibleInbox = computed(() => inboxes.value.length > 0);

const tableHeaders = computed(() => [
  t('CLIENT_SETTINGS.CURRENCY_MIGRATION.TABLE.INBOX'),
  t('CLIENT_SETTINGS.CURRENCY_MIGRATION.TABLE.MIGRATION'),
  t('CLIENT_SETTINGS.CURRENCY_MIGRATION.TABLE.STATUS'),
  t('CLIENT_SETTINGS.CURRENCY_MIGRATION.TABLE.CREATED_AT'),
  t('CLIENT_SETTINGS.CURRENCY_MIGRATION.TABLE.LAST_POLLED_AT'),
]);

const timestamp = value => (value ? new Date(value).toLocaleString() : '—');

const fetchMigrations = async () => {
  isFetching.value = true;
  try {
    const { data } = await WabaCurrencyMigrationsAPI.get();
    migrations.value = data.migrations;
    inboxes.value = data.inboxes;
    currencies.value = data.currencies;
  } catch (error) {
    useAlert(t('CLIENT_SETTINGS.CURRENCY_MIGRATION.API.FETCH_ERROR'));
  } finally {
    isFetching.value = false;
  }
};

const openMigration = id =>
  router.push({
    name: 'client_settings_currency_migration_show',
    params: { accountId: route.params.accountId, migrationId: id },
  });

// A created migration goes straight to its own page: the preflight checklist that starts
// it lives there, next to the guidance for that step.
const onCreate = async payload => {
  isCreating.value = true;
  try {
    const { data } = await WabaCurrencyMigrationsAPI.create(payload);
    createDialogRef.value?.close();
    useAlert(t('CLIENT_SETTINGS.CURRENCY_MIGRATION.API.CREATE_SUCCESS'));
    openMigration(data.id);
  } catch (error) {
    useAlert(
      migrationApiError(
        error,
        t('CLIENT_SETTINGS.CURRENCY_MIGRATION.API.CREATE_ERROR')
      )
    );
  } finally {
    isCreating.value = false;
  }
};

onMounted(fetchMigrations);
</script>

<template>
  <SettingsLayout
    :is-loading="isFetching"
    :loading-message="$t('CLIENT_SETTINGS.CURRENCY_MIGRATION.LOADING')"
  >
    <template #header>
      <BaseSettingsHeader
        :title="$t('CLIENT_SETTINGS.CURRENCY_MIGRATION.HEADER')"
        :description="$t('CLIENT_SETTINGS.CURRENCY_MIGRATION.DESCRIPTION')"
        icon-name="person-account"
      >
        <template #actions>
          <NextButton
            icon="i-lucide-circle-plus"
            :label="$t('CLIENT_SETTINGS.CURRENCY_MIGRATION.NEW')"
            :disabled="!hasEligibleInbox"
            data-testid="new-migration"
            @click="createDialogRef?.open()"
          />
        </template>
      </BaseSettingsHeader>
    </template>

    <template #preBody>
      <ClientSettingsTabs class="mb-6" />
    </template>

    <template #body>
      <p
        v-if="!hasEligibleInbox"
        class="text-sm text-n-amber-11"
        data-testid="no-eligible-inbox"
      >
        {{ $t('CLIENT_SETTINGS.CURRENCY_MIGRATION.NO_ELIGIBLE_INBOX') }}
      </p>

      <p
        v-else-if="!migrations.length"
        class="text-sm text-n-slate-11"
        data-testid="migrations-empty"
      >
        {{ $t('CLIENT_SETTINGS.CURRENCY_MIGRATION.EMPTY') }}
      </p>

      <table
        v-else
        class="min-w-full overflow-x-auto divide-y divide-n-weak"
        data-testid="migrations-table"
      >
        <thead>
          <tr>
            <th
              v-for="header in tableHeaders"
              :key="header"
              class="py-4 pr-4 text-sm font-semibold text-left text-n-slate-11"
            >
              {{ header }}
            </th>
          </tr>
        </thead>
        <tbody class="divide-y divide-n-weak">
          <tr
            v-for="migration in migrations"
            :key="migration.id"
            class="cursor-pointer hover:bg-n-alpha-1"
            :data-testid="`migration-row-${migration.id}`"
            @click="openMigration(migration.id)"
          >
            <td class="py-4 pr-4 text-sm text-n-slate-12">
              {{ migration.inbox_name }}
            </td>
            <td class="py-4 pr-4 text-sm text-n-slate-11">
              {{ migration.source_currency || '—' }} →
              {{ migration.target_currency }}
            </td>
            <td class="py-4 pr-4">
              <MigrationStatusBadge :status="migration.status" />
            </td>
            <td class="py-4 pr-4 text-sm text-n-slate-11">
              {{ timestamp(migration.created_at) }}
            </td>
            <td class="py-4 pr-4 text-sm text-n-slate-11">
              {{ timestamp(migration.last_polled_at) }}
            </td>
          </tr>
        </tbody>
      </table>

      <CreateMigrationDialog
        ref="createDialogRef"
        :inboxes="inboxes"
        :currencies="currencies"
        :is-creating="isCreating"
        @create="onCreate"
      />
    </template>
  </SettingsLayout>
</template>
